/*
 * HVACloud AI · Schema-Gruppierung — Task modal.
 *
 * POST /api/discovery/devices/{d}/suggest-schema → nested proposal +
 * optional leaf-rename overrides. Operator sees hierarchical preview,
 * can rename groups + leaves inline, then POST /apply-schema materialises
 * the whole thing (topological insert + leaf reparent + leaf rename) in
 * one transaction.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { AiTaskChrome, type AiTaskStage } from './AiTaskChrome';
import { api, ApiError } from '../../api/client';
import { useAsync } from '../../hooks/useAsync';
import type { PlantSchemaResponse } from '../../api/types';
import { HeroAiSchema } from '../../icons/HvacIcons';
import { BRAND } from '../../brand/brand';

interface Props {
  plantId: string;
  onClose(): void;
}

interface ProposalNode {
  symbol: string;
  displayName: string;
  leafIds: string[];
  parentSymbol: string | null;
}

interface LeafOverride {
  leafId: string;
  symbol: string;
  currentDisplayName: string | null;
  proposedDisplayName: string;
}

interface Proposal {
  deviceId: string;
  nodes: ProposalNode[];
  leafOverrides: LeafOverride[] | null;
  rationale: string | null;
}

interface TreeRow {
  node: ProposalNode;
  depth: number;
}

const PROGRESS_MESSAGES = [
  `${BRAND.aiName} analysiert die Datenpunkte…`,
  'Hierarchie wird aufgebaut (Wärmepumpen, Heizkreise, TWW, …)',
  'Verschachtelung wird geprüft…',
  'Fast fertig — Ergebnis wird zusammengestellt…',
];

export function AiSchemaModal({ plantId, onClose }: Props) {
  const [hint, setHint] = useState('');
  const [stage, setStage] = useState<AiTaskStage>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [deviceId, setDeviceId] = useState<string>('');
  // Per-leaf-override selection + edited value overrides. Keyed by leafId.
  const [selectedOverrides, setSelectedOverrides] = useState<Record<string, boolean>>({});
  const [editedOverrides, setEditedOverrides] = useState<Record<string, string>>({});

  const plantState = useAsync(
    (signal) => api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`, signal),
    [plantId],
  );

  const devices = plantState.status === 'ok' ? plantState.data.devices : [];

  useEffect(() => {
    if (deviceId === '' && devices.length > 0) setDeviceId(devices[0].id);
  }, [devices, deviceId]);

  const run = async () => {
    if (!deviceId) return;
    setStage('loading');
    setErrorMsg(null);
    try {
      const res = await api.post<Proposal>(
        `/api/discovery/devices/${deviceId}/suggest-schema`,
        { hint: hint.trim() || null });
      setProposal(res);
      // Default: all overrides selected, values pre-filled with the proposal
      const nextSel: Record<string, boolean> = {};
      const nextEd: Record<string, string> = {};
      for (const o of res.leafOverrides ?? []) {
        nextSel[o.leafId] = true;
        nextEd[o.leafId] = o.proposedDisplayName;
      }
      setSelectedOverrides(nextSel);
      setEditedOverrides(nextEd);
      setStage('result');
    } catch (e) {
      setErrorMsg(explain(e));
      setStage('error');
    }
  };

  const apply = async () => {
    if (!proposal) return;
    setStage('applying');
    setErrorMsg(null);
    try {
      const leafOverrides = (proposal.leafOverrides ?? [])
        .filter((o) => selectedOverrides[o.leafId])
        .map((o) => ({
          leafId: o.leafId,
          displayName: (editedOverrides[o.leafId] ?? o.proposedDisplayName).trim(),
        }))
        .filter((o) => o.displayName !== '');

      await api.post<{ nodesCreated: number; leavesMoved: number; leavesRenamed: number }>(
        `/api/discovery/devices/${proposal.deviceId}/apply-schema`,
        {
          nodes: proposal.nodes.map((n) => ({
            symbol: n.symbol,
            displayName: n.displayName.trim() === '' ? n.symbol : n.displayName.trim(),
            leafIds: n.leafIds,
            parentSymbol: n.parentSymbol,
          })),
          leafOverrides: leafOverrides.length > 0 ? leafOverrides : null,
        });
      onClose();
    } catch (e) {
      setErrorMsg(explain(e));
      setStage('result');
    }
  };

  const updateName = (symbol: string, value: string) => {
    if (!proposal) return;
    setProposal({
      ...proposal,
      nodes: proposal.nodes.map((n) => n.symbol === symbol ? { ...n, displayName: value } : n),
    });
  };

  // Build the tree render order — depth-first, roots (parentSymbol=null) first,
  // then children in the order the server returned them (already topologically
  // sorted, so we just walk once). Empty subtrees have already been pruned server-side.
  const rows = useMemo<TreeRow[]>(() => {
    if (!proposal) return [];
    const children = new Map<string | null, ProposalNode[]>();
    for (const n of proposal.nodes) {
      const key = n.parentSymbol ?? null;
      if (!children.has(key)) children.set(key, []);
      children.get(key)!.push(n);
    }
    const out: TreeRow[] = [];
    const walk = (parent: string | null, depth: number) => {
      const kids = children.get(parent) ?? [];
      for (const k of kids) {
        out.push({ node: k, depth });
        walk(k.symbol, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [proposal]);

  const totalLeaves = proposal?.nodes.reduce((s, n) => s + n.leafIds.length, 0) ?? 0;
  const overrideCount = proposal?.leafOverrides?.length ?? 0;
  const selectedOverrideCount = proposal?.leafOverrides?.filter((o) => selectedOverrides[o.leafId]).length ?? 0;

  return (
    <AiTaskChrome
      title={`${BRAND.aiName} · Schema-Gruppierung`}
      hint={hint}
      onHintChange={setHint}
      hintPlaceholder="z.B.: 'TWW-Leaves gehören unter Warmwasser', 'Steigungswerte sind K statt °C'"
      stage={stage}
      errorMessage={errorMsg}
      progressMessages={PROGRESS_MESSAGES}
      onClose={onClose}
      hero={<HeroAiSchema size={96} style={{ color: 'var(--ai-accent)' }} />}
      chatContext={{ mode: 'schema', plantId, deviceId: deviceId || undefined }}
      footer={
        stage === 'result' ? (
          <>
            <span class="muted" style={{ marginRight: 'auto' }}>
              {proposal?.nodes.length ?? 0} Knoten, {totalLeaves} Datenpunkte
              {overrideCount > 0 && `, ${selectedOverrideCount}/${overrideCount} Umbenennungen`}
            </span>
            <button type="button" class="hv-btn" onClick={onClose}>Abbrechen</button>
            <button type="button" class="hv-btn hv-btn--primary" onClick={apply}>Übernehmen</button>
          </>
        ) : stage === 'applying' ? (
          <button type="button" class="hv-btn" disabled>Übernehme…</button>
        ) : (
          <>
            <div style={{ flex: 1 }} />
            <button type="button" class="hv-btn" onClick={onClose} disabled={stage === 'loading'}>
              Abbrechen
            </button>
            <button type="button" class="hv-btn hv-btn--primary" onClick={run}
                    disabled={!deviceId || stage === 'loading' || plantState.status !== 'ok'}>
              {stage === 'loading' ? `${BRAND.aiName} arbeitet…` : 'Vorschlagen'}
            </button>
          </>
        )
      }
    >
      {stage === 'result' && proposal && (
        <>
          {proposal.rationale && <p class="hv-ai-modal__rationale">{proposal.rationale}</p>}

          <h4 style={{ marginTop: 0 }}>Hierarchie ({proposal.nodes.length} Knoten)</h4>
          <table class="hv-data-table">
            <thead>
              <tr>
                <th>Anzeigename</th>
                <th style={{ width: '30%' }}>Symbol</th>
                <th style={{ width: '5em', textAlign: 'right' }}>Leaves</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ node, depth }) => (
                <tr key={node.symbol}>
                  <td style={{ paddingLeft: `${depth * 1.4}em` }}>
                    <span class="muted" style={{ marginRight: '0.4em' }}>
                      {depth > 0 ? '↳' : '•'}
                    </span>
                    <input
                      type="text"
                      value={node.displayName}
                      onInput={(e) => updateName(node.symbol, (e.target as HTMLInputElement).value)}
                      style={{ width: `calc(100% - ${depth * 1.4 + 1.5}em)` }}
                    />
                  </td>
                  <td class="mono">{node.symbol}</td>
                  <td style={{ textAlign: 'right' }}>{node.leafIds.length}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {proposal.leafOverrides && proposal.leafOverrides.length > 0 && (
            <>
              <h4>Vorgeschlagene Umbenennungen ({proposal.leafOverrides.length})</h4>
              <p class="muted" style={{ marginTop: 0 }}>
                {BRAND.productName} kürzt Anzeigenamen, wenn die neue Node-Struktur die Info schon trägt. Deselektiere Zeilen, die du behalten willst.
              </p>
              <table class="hv-data-table">
                <thead>
                  <tr>
                    <th style={{ width: '2em' }}></th>
                    <th>Symbol</th>
                    <th style={{ width: '30%' }}>Aktuell</th>
                    <th style={{ width: '30%' }}>Vorgeschlagen</th>
                  </tr>
                </thead>
                <tbody>
                  {proposal.leafOverrides.map((o) => (
                    <tr key={o.leafId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!selectedOverrides[o.leafId]}
                          onChange={(e) => setSelectedOverrides((s) => ({ ...s, [o.leafId]: (e.target as HTMLInputElement).checked }))}
                        />
                      </td>
                      <td class="mono">{o.symbol}</td>
                      <td class="muted"><s>{o.currentDisplayName ?? o.symbol}</s></td>
                      <td>
                        <input
                          type="text"
                          value={editedOverrides[o.leafId] ?? o.proposedDisplayName}
                          onInput={(e) => setEditedOverrides((v) => ({ ...v, [o.leafId]: (e.target as HTMLInputElement).value }))}
                          style={{ width: '100%' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}

      {(stage === 'idle' || stage === 'error') && (
        <div class="hv-modal__field">
          <label>Device</label>
          <select value={deviceId}
                  onChange={(e) => setDeviceId((e.target as HTMLSelectElement).value)}
                  disabled={devices.length === 0}>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>{d.name} ({d.symbol})</option>
            ))}
            {devices.length === 0 && <option value="">— keine Devices —</option>}
          </select>
        </div>
      )}
    </AiTaskChrome>
  );
}

function explain(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string; detail?: string; title?: string } | undefined;
    return body?.error ?? body?.detail ?? body?.title ?? `Fehler ${e.status}`;
  }
  if (e instanceof Error) return e.message;
  return 'Unbekannter Fehler.';
}
